import React, { useState } from 'react';
import { TestResults, TestExecution, TestStepResult } from '../SecretTestRunner';

interface TestResultsPanelProps {
  results?: TestResults;
  execution: TestExecution;
}

export const TestResultsPanel: React.FC<TestResultsPanelProps> = ({
  results,
  execution
}) => {
  const [selectedTest, setSelectedTest] = useState<TestStepResult | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'screenshots'>('overview');

  if (!results) {
    return (
      <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Test Results
        </h2>
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {execution.status === 'running' ? 'Tests are running...' : 'No test results available'}
          </p>
        </div>
      </div>
    );
  }

  const successRate = results.passed + results.failed > 0 
    ? Math.round((results.passed / (results.passed + results.failed)) * 100) 
    : 0;

  return (
    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Test Results
          </h2>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            execution.status === 'completed' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : execution.status === 'failed'
              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
          }`}>
            {execution.status === 'completed' ? 'Completed' : 
             execution.status === 'failed' ? 'Failed' : 'Running'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4">
        <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-600">
          {['overview', 'details', 'screenshots'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {results.passed}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">Passed</div>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {results.failed}
                </div>
                <div className="text-sm text-red-600 dark:text-red-400">Failed</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {results.skipped}
                </div>
                <div className="text-sm text-yellow-600 dark:text-yellow-400">Skipped</div>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {successRate}%
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">Success Rate</div>
              </div>
            </div>

            {/* Duration and Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Execution Time</h3>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {Math.floor(results.duration / 60)}:{(Math.floor(results.duration) % 60).toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total duration
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Test Suite</h3>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {execution.scenarios.length} scenarios executed
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {execution.startTime && `Started at ${execution.startTime.toLocaleTimeString()}`}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="space-y-4">
            {results.details.map((detail, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  detail.status === 'passed'
                    ? 'border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                    : detail.status === 'failed'
                    ? 'border-red-200 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                    : 'border-yellow-200 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20'
                } hover:shadow-md`}
                onClick={() => setSelectedTest(detail)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      detail.status === 'passed'
                        ? 'bg-green-500'
                        : detail.status === 'failed'
                        ? 'bg-red-500'
                        : 'bg-yellow-500'
                    }`}>
                      {detail.status === 'passed' ? (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : detail.status === 'failed' ? (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {detail.scenario}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Duration: {Math.round(detail.duration / 1000)}s
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      detail.status === 'passed'
                        ? 'text-green-600 dark:text-green-400'
                        : detail.status === 'failed'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`}>
                      {detail.status.toUpperCase()}
                    </div>
                    {detail.screenshots && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {detail.screenshots.length} screenshots
                      </div>
                    )}
                  </div>
                </div>
                {detail.error && (
                  <div className="mt-3 p-3 bg-red-100 dark:bg-red-900/30 rounded text-sm text-red-800 dark:text-red-300">
                    {detail.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'screenshots' && (
          <div className="space-y-4">
            {results.details.filter(d => d.screenshots && d.screenshots.length > 0).length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No screenshots available
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {results.details.filter(d => d.screenshots && d.screenshots.length > 0).map((detail, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                      {detail.scenario}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {detail.screenshots?.map((screenshot, screenshotIndex) => (
                        <div
                          key={screenshotIndex}
                          className="aspect-video bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400"
                        >
                          📷 {screenshot}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Export/Download Actions */}
      <div className="px-6 pb-6">
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
            Download Report
          </button>
          <button className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm">
            Export JSON
          </button>
        </div>
      </div>
    </div>
  );
};